import { CreateTrainingForUserRequest, CustomActionDto, TrainingSelectionStrategy } from '@ampsec/platform-client';
import { AmplifierTrainingTemplateKey, getAmplifierTrainingTemplateByKey } from './amplifierTrainingTemplates';
import { ComplianceTrainingModuleOption } from './useComplianceTrainingModules';
import { getPlatformTrainingCreateUrl } from './platformTrainingUrl';

export interface BuildAmplifierTrainingRestActionInput {
    displayValue: string;
    description?: string;
    templateKey: AmplifierTrainingTemplateKey;
    selectedSubjects: string[];
    modules: ComplianceTrainingModuleOption[];
    retryStrategy?: CustomActionDto['retryStrategy'];
}

const toTrainingModuleRequest = (module: ComplianceTrainingModuleOption) => ({
    subject: module.subject,
    videoUrl: module.videoUrl,
    ...(module.thumbnailUrl ? { thumbnailUrl: module.thumbnailUrl } : {}),
    ...(module.description ? { description: module.description } : {}),
});

export const buildCreateTrainingPayload = (
    templateKey: AmplifierTrainingTemplateKey,
    selectedSubjects: string[],
    modules: ComplianceTrainingModuleOption[],
): CreateTrainingForUserRequest => {
    const template = getAmplifierTrainingTemplateByKey(templateKey);
    if (!template) {
        throw new Error(`Unknown amplifier training template: ${templateKey}`);
    }

    if (template.selectionStrategy === TrainingSelectionStrategy.Scheduled) {
        return { selectionStrategy: TrainingSelectionStrategy.Scheduled };
    }

    const selectedModules = selectedSubjects
        .map((subject) => modules.find((module) => module.subject === subject))
        .filter((module): module is ComplianceTrainingModuleOption => Boolean(module));

    if (!selectedModules.length) {
        throw new Error('Select at least one enabled compliance training module');
    }

    if (template.selectionStrategy === TrainingSelectionStrategy.Module) {
        return {
            selectionStrategy: TrainingSelectionStrategy.Module,
            trainingRequest: toTrainingModuleRequest(selectedModules[0]),
        };
    }

    return {
        selectionStrategy: TrainingSelectionStrategy.Custom,
        trainingRequests: selectedModules.map(toTrainingModuleRequest),
    };
};

export const buildAmplifierTrainingRestAction = ({
    displayValue,
    description,
    templateKey,
    selectedSubjects,
    modules,
    retryStrategy,
}: BuildAmplifierTrainingRestActionInput): CustomActionDto => {
    const payload = buildCreateTrainingPayload(templateKey, selectedSubjects, modules);

    return {
        id: '',
        displayValue,
        description,
        kind: 'REST_ACTION',
        isTemplate: false,
        meta: {
            request: {
                method: 'POST',
                url: getPlatformTrainingCreateUrl(),
                params: {},
                headers: {
                    'Content-Type': 'application/json',
                },
                payload: {
                    kind: 'TEMPLATE',
                    format: 'JSON',
                    value: JSON.stringify(payload, null, 2),
                },
            },
        },
        retryStrategy: retryStrategy ?? {
            kind: 'CONSTANT_BACKOFF',
            maxRetries: 0,
            delay: 0,
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        deletedAt: null,
    };
};
